import { Box, Divider, Fade } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { Fragment, useMemo, useState } from "react"

import { CARRIER_PREFERENCE_TABS } from "../../constants/carrierPreferences"
import { useGetCarrierPreferences } from "../../hooks/useGetCarrierPreferences"
import { CompanyPreferenceSection } from "../CompanyPreferenceSection"

import { CarrierPreferenceSection } from "./CarrierPreferenceSection"
import { EditCarrierPreferencesDialog } from "./EditCarrierPreferencesDialog"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import { PermissionActionType } from "@/constants/permissions"
import { usePermissionManager } from "@/hooks/usePermissionManager"

interface CarrierPreferencesProps {
  locationId: string
}

export const CarrierPreferences: FC<CarrierPreferencesProps> = ({ locationId }) => {
  const { preferences, isFetchingPreferences, refetchPreferences } = useGetCarrierPreferences(locationId)
  const [isDialogOpen, setIsModalOpen] = useState(false)
  const { hasPermissions } = usePermissionManager()

  const canEdit = hasPermissions([PermissionActionType.CUSTOMER_MAINTENANCE])

  const sortedData = useMemo(() => {
    if (!preferences) return []
    return [...preferences].sort((a, b) => {
      return CARRIER_PREFERENCE_TABS.indexOf(a.carrierType) - CARRIER_PREFERENCE_TABS.indexOf(b.carrierType)
    })
  }, [preferences])

  const handleOpenDialog = () => {
    setIsModalOpen(true)
  }

  const handleCloseDialog = (shouldRefetch = false) => {
    setIsModalOpen(false)
    if (shouldRefetch) {
      refetchPreferences()
    }
  }

  const hasData = preferences?.length

  return (
    <>
      {!hasData ? (
        <Fade in unmountOnExit timeout={500}>
          <InformationMissingAlert
            title={canEdit ? "Start by entering preferences for a carrier" : "No configuration details have been added"}
            action={
              canEdit && (
                <Button
                  color='inherit'
                  size='small'
                  appearance='outlined'
                  className='text-primary'
                  startIcon={<AddOutlined />}
                  onClick={handleOpenDialog}
                  data-testid='carrier-preferences-add-button'
                >
                  Add Carrier Preferences
                </Button>
              )
            }
          />
        </Fade>
      ) : (
        <CompanyPreferenceSection
          title='Carrier Preferences'
          onEdit={handleOpenDialog}
          canEdit={canEdit}
          editButtonTestId='carrier-preferences-edit-button'
          isLoading={isFetchingPreferences}
        >
          <Box className='flex flex-col' data-testid='carrier-preferences-list'>
            {sortedData.map((preference, index) => (
              <Fragment key={preference.carrierType}>
                <CarrierPreferenceSection preference={preference} />
                {index < sortedData.length - 1 && <Divider className='mb-2' />}
              </Fragment>
            ))}
          </Box>
        </CompanyPreferenceSection>
      )}

      {isDialogOpen && (
        <EditCarrierPreferencesDialog
          onClose={handleCloseDialog}
          locationId={locationId}
          initialData={preferences || []}
        />
      )}
    </>
  )
}
